import { useCallback, useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { api, type ConversationSummary, type Thread } from '../../api.ts';
import { Chat } from '../../components/Chat.tsx';
import { avatarHue, initials, presence, whenShort } from '../../lib/people.ts';

/**
 * The teacher's messages: every pupil down the left, the open conversation on the right.
 *
 * Shaped like the messenger everybody already knows, because a teacher between two lessons has no
 * attention to spare for learning a new one. Nothing here is novel on purpose.
 *
 * The list is every pupil, not only those who have written. A child who has gone quiet for three
 * weeks is worth noticing, and a list that hides them cannot show that.
 *
 * Opening a thread marks it read on the server, so the badge clears by being looked at rather
 * than by a separate "mark as read" nobody would press.
 */
const POLL_MS = 15_000;

/** Below this the two panes cannot both fit, so the thread covers the list, phone-style. */
const NARROW = '(max-width: 760px)';

export function ConversationsPage(): JSX.Element {
  const [conversations, setConversations] = useState<ConversationSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<Thread | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);

  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const onChange = (e: MediaQueryListEvent): void => setNarrow(e.matches);

    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const loadList = useCallback(async (): Promise<void> => {
    try {
      setConversations(await api.authoring.conversations());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить переписку');
    }
  }, []);

  const loadThread = useCallback(async (studentProfileId: string): Promise<void> => {
    try {
      setThread(await api.authoring.thread(studentProfileId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось открыть диалог');
    }
  }, []);

  useEffect(() => {
    void loadList();
    const timer = setInterval(() => void loadList(), POLL_MS);
    return () => clearInterval(timer);
  }, [loadList]);

  useEffect(() => {
    if (!selected) return undefined;

    void loadThread(selected);
    const timer = setInterval(() => void loadThread(selected), POLL_MS);
    return () => clearInterval(timer);
  }, [selected, loadThread]);

  const rows = conversations ?? [];

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((c) => c.student_name.toLowerCase().includes(needle));
  }, [rows, query]);

  const waiting = rows.reduce((sum, c) => sum + c.unread, 0);

  function open(studentProfileId: string): void {
    setSelected(studentProfileId);
    // Clear the badge at once. The server agrees on the next poll, and waiting for it would leave
    // a number on screen beside a conversation the teacher is already reading.
    setConversations((current) =>
      (current ?? []).map((row) =>
        row.student_profile_id === studentProfileId ? { ...row, unread: 0 } : row,
      ),
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>
          Сообщения
          {waiting > 0 ? <span className="page-head__count">{waiting}</span> : null}
        </h1>
        <p className="muted">
          У каждого ученика один диалог на школу — отвечать может любой преподаватель.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {conversations === null && !error ? <p className="muted">Загружаем…</p> : null}

      {conversations?.length === 0 ? (
        <div className="notice">
          Учеников пока нет. Добавьте их на вкладке «Ученики» — тогда появится, с кем говорить.
        </div>
      ) : null}

      {rows.length > 0 ? (
        // On a phone one pane at a time: the list, or the conversation you tapped.
        <div className={`dialogs${narrow && selected ? ' dialogs--open' : ''}`}>
          <div className="dialogs__side">
            <div className="dialogs__search">
              <input
                className="input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск по имени"
                aria-label="Поиск ученика"
              />
            </div>

            <div className="dialogs__list">
              {found.length === 0 ? (
                <p className="muted dialogs__none">Никого с таким именем.</p>
              ) : null}

              {found.map((c) => (
                <button
                  key={c.student_profile_id}
                  type="button"
                  className={`dialogs__item${
                    selected === c.student_profile_id ? ' dialogs__item--active' : ''
                  }`}
                  onClick={() => open(c.student_profile_id)}
                >
                  {/*
                   * A dot on the avatar: a teacher writing to somebody needs to know whether an
                   * answer is likely in a minute or tomorrow. Without it every reply goes into
                   * the same silence.
                   */}
                  <span className="avatar-wrap">
                    <span
                      className="avatar"
                      style={{ '--hue': avatarHue(c.student_name) } as CSSProperties}
                      aria-hidden="true"
                    >
                      {initials(c.student_name)}
                    </span>
                    <span
                      className={`avatar__dot avatar__dot--${presence(c.last_seen_at).state}`}
                      title={presence(c.last_seen_at).label}
                    />
                  </span>

                  <span className="dialogs__text">
                    <span className="dialogs__line">
                      <span className="dialogs__name">{c.student_name}</span>
                      {c.last_message_at ? (
                        <span className="dialogs__when">{whenShort(c.last_message_at)}</span>
                      ) : null}
                    </span>

                    <span className="dialogs__line">
                      <span
                        className={`dialogs__preview${
                          c.unread > 0 ? ' dialogs__preview--unread' : ''
                        }`}
                      >
                        {c.last_message_preview === null ? (
                          <em>переписки ещё не было</em>
                        ) : (
                          <>
                            {/* Whose words these are, the way every messenger says it. */}
                            {c.last_from_student ? null : (
                              <span className="dialogs__mine">Вы: </span>
                            )}
                            {c.last_message_preview}
                          </>
                        )}
                      </span>
                      {c.unread > 0 ? <span className="dialogs__badge">{c.unread}</span> : null}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="dialogs__thread">
            {selected && thread ? (
              <>
                <div className="dialogs__head">
                  {narrow ? (
                    <button
                      type="button"
                      className="dialogs__back"
                      onClick={() => setSelected(null)}
                      aria-label="К списку учеников"
                    >
                      ←
                    </button>
                  ) : null}

                  <span
                    className="avatar avatar--small"
                    style={{ '--hue': avatarHue(thread.student_name) } as CSSProperties}
                    aria-hidden="true"
                  >
                    {initials(thread.student_name)}
                  </span>
                  <span className="dialogs__title">{thread.student_name}</span>
                  {(() => {
                    const who = conversations?.find((c) => c.student_profile_id === selected);
                    if (!who) return null;

                    const here = presence(who.last_seen_at);
                    return <span className={`presence presence--${here.state}`}>{here.label}</span>;
                  })()}
                </div>

                <Chat
                  messages={thread.messages}
                  mineAreFromStudent={false}
                  placeholder="Ответ ученику"
                  onSend={async (body) => {
                    const sent = await api.authoring.reply(selected, body);
                    setThread((current) =>
                      current ? { ...current, messages: [...current.messages, sent] } : current,
                    );
                    void loadList();
                  }}
                />
              </>
            ) : (
              <div className="dialogs__blank">
                <p className="muted">Выберите ученика слева, чтобы открыть переписку.</p>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
